import { Messages } from '~src/codegen/Localize';
import { Tableau } from '~src/common/utilities/TableauLog';
import { ErrorReason } from '~src/sessionManagement/SessionManagementService';
import { TagRequest } from '~src/ts/ServerApi/requests/TagRequest';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { IRequest } from './serverApi/types/IRequest';
import { IResult } from './serverApi/types/IResponse';
import TagHttpClient from './TagHttpClient';
import WebHttpClient from './WebHttpClient';


export const ERROR_NOTIFYING_SERVER_RESPONSE_DELAY = 2000;
class WebHttpClientWithErrorToast {
  private notification(reason: any, showToast: boolean){
    let errorMessage = Messages.unexpectedError();
    if (reason.errors && reason.errors.length > 0 && reason.errors[0].message){
      errorMessage = errorMessage.concat('\n ' + reason.errors[0].message);
    }
    Tableau.Logger().error('Unknown error from server: ' + errorMessage);
    if (showToast) {
      ToasterService.instance.pop({ type: ToastType.Error, text: errorMessage});
    }
  }

  // this will throw a toast if the http call itself fails, not if the server returns a
  // valid http response that says it had an internal error
  public notifyOnFail<TResult>(result: Promise<TResult>): Promise<TResult> {
    result.then((data: any) => {
      if (data.errors && data.errors.length > 0) {
        this.notification(data, false);
      }
      return data;
    },
      (reason: ErrorReason) => {
        if (reason === ErrorReason.Unknown) {
          this.notification(reason, true);
        }
      });
    return result;
  }

  public addDelay<T>(result: Promise<T>): Promise<T> {
    return new Promise<T>(resolve =>
      setTimeout(() => resolve(result), ERROR_NOTIFYING_SERVER_RESPONSE_DELAY)
    );
  }

  public sendRequest<TParams, TResult extends IResult>(
    request: IRequest<TParams, TResult>
  ): Promise<TResult> {
    return this.notifyOnFail(WebHttpClient.sendRequest(request));
  }

  public sendTagRequest<TParams, TResult extends IResult>(
    request: TagRequest<TParams, TResult>
  ): Promise<TResult> {
    return this.notifyOnFail(TagHttpClient.sendTagRequest(request));
  }

  public sendRequestAndDelay<TParams, TResult extends IResult>(
    request: IRequest<TParams, TResult>
  ): Promise<TResult> {
    return this.addDelay(this.sendRequest(request));
  }

  public sendRequests<TItem, TParams, TResult>(
    requests: IRequest<TParams, TResult>[],
    combineResults: (results: TResult[]) => TResult
  ): Promise<TResult> {
    return this.notifyOnFail(WebHttpClient.sendRequests(requests, combineResults));
  }

  public sendRequestsAndDelay<TItem, TParams, TResult>(
    requests: IRequest<TParams, TResult>[],
    combineResults: (results: TResult[]) => TResult
  ): Promise<TResult> {
    return this.addDelay(this.sendRequests(requests, combineResults));
  }
}

export type { WebHttpClientWithErrorToast };
export const WebHttpClientWithErrorToastInstance = new WebHttpClientWithErrorToast();
